<template>
  <div class="withholding-confirm">
    <!-- 费用说明信息区 -->
    <template v-if="pageState === 'pending'">
      <div class="page-content">
        <!-- 资质信息卡片 -->
        <div class="info-card">
          <div class="info-row">
            <span class="info-label">合作商名称</span>
            <span class="info-value">{{ detail.agentName }}</span>
          </div>
          <div class="info-divider"></div>
          <div class="info-row">
            <span class="info-label">商家资质</span>
            <span class="info-value">{{ detail.qualificationName }}</span>
          </div>
          <div class="info-divider"></div>
          <div class="info-row">
            <span class="info-label">是否开发票</span>
            <span class="info-value">{{ detail.provideInvoice === 1 ? '是' : '否' }}</span>
          </div>
          <div class="info-divider"></div>
          <div class="info-row">
            <span class="info-label">代扣服务费</span>
            <span class="info-value info-value--rate">{{ displayRate }}%</span>
          </div>
          <template>
            <div class="info-divider"></div>
            <div class="info-row info-row--block">
              <span class="info-label">代扣说明</span>
              <p class="info-desc">
                由于贵公司无法向合作商提供发票或提供发票不满足合作商要求，美团将在打款时代合作商扣除"代扣服务费"（具体费率由双方商议确定），后续该笔金额会转交给合作商。
              </p>
            </div>
          </template>
        </div>

        <!-- 门店列表区 -->
        <div class="poi-section">
          <div class="poi-section-header">
            <span class="poi-section-title">门店列表</span>
            <span class="poi-section-count">共{{ totalPoiCount }}家</span>
          </div>

          <div
            v-for="poi in poiList"
            :key="poi.poiId"
            class="poi-card"
          >
            <div class="poi-name">
              {{ poi.poiName }}
            </div>
            <div class="poi-meta">
              POI ID：{{ poi.poiId }}
            </div>
            <div class="poi-address">
              {{ poi.address }}
            </div>
          </div>

          <van-empty
            v-if="!poiLoading && !poiList.length"
            image="search"
            description="暂无门店数据"
          />
        </div>

        <!-- 门店列表分页 -->
        <div
          v-if="poiTotalPage > 1"
          class="poi-pagination"
        >
          <span
            class="poi-page-btn"
            :class="{ disabled: poiPageNum <= 1 }"
            @click="changePoiPage(-1)"
          >
            上一页
          </span>
          <span
            v-for="p in displayPages"
            :key="p"
            class="poi-page-num"
            :class="{ active: p === poiPageNum }"
            @click="gotoPoiPage(p)"
          >
            {{ p }}
          </span>
          <span
            class="poi-page-btn"
            :class="{ disabled: poiPageNum >= poiTotalPage }"
            @click="changePoiPage(1)"
          >
            下一页
          </span>
        </div>
      </div>

      <!-- 底部操作按钮 -->
      <div class="footer-actions">
        <van-button
          plain
          round
          type="default"
          class="footer-btn footer-btn--later"
          :loading="postponeLoading"
          @click="onPostpone"
        >
          稍后签约
        </van-button>
        <van-button
          round
          type="info"
          class="footer-btn footer-btn--confirm"
          :loading="confirmLoading"
          @click="onConfirm"
        >
          已阅读并确认
        </van-button>
      </div>
    </template>

    <!-- 已操作状态 -->
    <div
      v-else-if="pageState === 'done'"
      class="result-page"
    >
      <van-icon
        name="checked"
        class="result-icon result-icon--success"
      />
      <p class="result-text">
        已完成确认
      </p>
      <p class="result-sub">
        代扣服务费已生效，感谢您的配合。
      </p>
    </div>

    <!-- 链接无效 / 参数缺失 -->
    <div
      v-else-if="pageState === 'invalid'"
      class="result-page"
    >
      <div>链接已失效或参数有误，请联系合作商重新发送</div>
    </div>
  </div>
</template>

<script>
import Dialog from 'vant/lib/dialog';
import Button from 'vant/lib/button';
import Toast from 'vant/lib/toast';
import BillApi from '@/api/bill';
import 'vant/lib/dialog/style';
import 'vant/lib/button/style';

export default {
  name: 'WithholdingConfirm',
  components: {
    [Button.name]: Button,
  },
  data() {
    return {
      pageState: 'loading', // 'loading' | 'pending' | 'done' | 'invalid'
      detail: {
        id: 0,
        customerId: 0,
        qualificationName: '',
        agentName: '',
        provideInvoice: 2,
        withholdRate: 0,
        confirmStatus: 0, // 0: 待确认, 1: 已确认
      },
      poiList: [],
      poiLoading: false,
      totalPoiCount: 0,
      poiPageNum: 1,
      poiPageSize: 5,
      confirmLoading: false,
      postponeLoading: false,
    };
  },
  computed: {
    feeId() {
      return Number(this.$route.query.id) || 0;
    },
    displayRate() {
      return (this.detail.withholdRate / 100).toFixed(2);
    },
    poiTotalPage() {
      return Math.max(Math.ceil(this.totalPoiCount / this.poiPageSize), 1);
    },
    displayPages() {
      const total = this.poiTotalPage;
      if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
      const cur = this.poiPageNum;
      let start = Math.max(cur - 2, 1);
      const end = Math.min(start + 4, total);
      start = Math.max(end - 4, 1);
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
  },
  created() {
    this.init();
  },
  methods: {
    async init() {
      if (!this.feeId) {
        this.pageState = 'invalid';
        return;
      }
      await this.fetchFeeDetail();
    },
    async fetchFeeDetail() {
      try {
        const res = await BillApi.queryWithholdingFeeDetail(this.feeId);
        if (res.data) {
          const data = res.data;
          this.detail = {
            id: data.id || this.feeId,
            customerId: data.customerId || 0,
            qualificationName: data.qualificationName || '',
            agentName: data.agentName || '',
            provideInvoice: data.provideInvoice || 2,
            withholdRate: data.withholdRate || 0,
            confirmStatus: data.confirmStatus || 0,
          };
          // 如果已确认，直接跳转到完成状态
          if (data.confirmStatus === 1) {
            this.pageState = 'done';
          } else {
            this.pageState = 'pending';
            await this.fetchPoiList();
          }
        } else {
          this.pageState = 'invalid';
        }
      } catch (error) {
        console.error('Failed to fetch fee detail:', error);
        this.pageState = 'invalid';
      }
    },
    async fetchPoiList() {
      this.poiLoading = true;
      try {
        const res = await BillApi.queryAgentInvoicePoiList(this.feeId, this.poiPageNum, this.poiPageSize);
        if (res.data) {
          this.totalPoiCount = res.data.total || 0;
          this.poiList = res.data.list || [];
        }
      } catch (error) {
        console.error('Failed to fetch poi list:', error);
        Toast.fail('加载门店列表失败');
      } finally {
        this.poiLoading = false;
      }
    },
    async changePoiPage(delta) {
      const next = this.poiPageNum + delta;
      if (next < 1 || next > this.poiTotalPage) return;
      this.poiPageNum = next;
      await this.fetchPoiList();
    },
    async gotoPoiPage(p) {
      if (p === this.poiPageNum) return;
      this.poiPageNum = p;
      await this.fetchPoiList();
    },
    async onPostpone() {
      try {
        await Dialog.confirm({
          title: '稍后签约',
          message: '确认稍后再处理？您可通过短信链接重新进入该页面完成确认。',
          confirmButtonText: '确认',
          cancelButtonText: '取消',
        });
      } catch {
        return;
      }
      this.$router.go(-1);
    },
    async onConfirm() {
      try {
        await Dialog.confirm({
          title: '确认代扣服务费',
          message: `确认同意代扣服务费 ${this.displayRate}%？确认后将立即生效。`,
          confirmButtonText: '确认',
          cancelButtonText: '取消',
        });
      } catch {
        return;
      }

      this.confirmLoading = true;
      try {
        const res = await BillApi.confirmAgentWithholding(this.feeId);
        if (res.data) {
          this.pageState = 'done';
        } else {
          Toast.fail(res.error.msg || '确认失败，请重试');
        }
      } catch (error) {
        console.error('Failed to confirm:', error);
        Toast.fail('确认失败，请重试');
      } finally {
        this.confirmLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.withholding-confirm {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 120px;

  // 顶部导航栏
  .nav-bar {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 88px;
    background: #fff;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    flex-shrink: 0;

    &__back {
      position: absolute;
      left: 24px;
      font-size: 28px;
      color: rgba(0, 0, 0, 0.84);
      cursor: pointer;
    }

    &__title {
      font-size: 28px;
      font-weight: 600;
      color: rgba(0, 0, 0, 0.84);
    }
  }

  .page-content {
    padding: 24px 16px;
  }

  // 资质信息卡片
  .info-card {
    background: #fff;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 24px;
  }

  .info-row {
    display: flex;
    align-items: flex-start;
    padding: 24px 0;
    font-size: 24px;

    &--block {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  .info-label {
    color: rgba(0, 0, 0, 0.42);
    width: 140px;
    flex-shrink: 0;
    font-weight: 400;
    font-size: 24px;
  }

  .info-value {
    color: rgba(0, 0, 0, 0.84);
    font-weight: 400;
    flex: 1;
    text-align: left;
    font-size: 24px;

    &--rate {
      font-size: 36px;
      font-weight: 600;
      color: rgba(0, 0, 0, 0.84);
      text-align: left;
    }
  }

  .info-desc {
    margin: 12px 0 0;
    font-size: 22px;
    color: rgba(0, 0, 0, 0.42);
    line-height: 1.8;
    text-align: left;
    padding: 0;
  }

  .info-divider {
    height: 1px;
    background: rgba(0, 0, 0, 0.06);
  }

  // 门店列表区
  .poi-section {
    background: #fff;
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 24px;
    margin-left: 0;
    margin-right: 0;
  }

  .poi-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);

    .poi-section-title {
      font-size: 26px;
      font-weight: 600;
      color: rgba(0, 0, 0, 0.84);
    }

    .poi-section-count {
      font-size: 22px;
      color: #1989fa;
      font-weight: 400;
    }
  }

  .poi-card {
    text-align: left;
    padding: 24px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);

    &:last-child {
      border-bottom: none;
    }

    .poi-name {
      font-size: 26px;
      font-weight: 600;
      color: rgba(0, 0, 0, 0.84);
      margin-bottom: 8px;
    }

    .poi-meta {
      font-size: 22px;
      color: rgba(0, 0, 0, 0.42);
      margin-bottom: 6px;
      font-weight: 400;
    }

    .poi-address {
      font-size: 22px;
      color: rgba(0, 0, 0, 0.42);
      font-weight: 400;
    }
  }

  // 门店分页
  .poi-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    gap: 16px;
    background: #fff;
    border-radius: 12px;
    margin-bottom: 24px;

    .poi-page-btn {
      padding: 16px 24px;
      font-size: 22px;
      color: #1989fa;
      cursor: pointer;
      font-weight: 500;

      &.disabled {
        color: rgba(0, 0, 0, 0.2);
        pointer-events: none;
      }
    }

    .poi-page-num {
      width: 64px;
      height: 64px;
      line-height: 64px;
      text-align: center;
      border-radius: 8px;
      font-size: 22px;
      color: rgba(0, 0, 0, 0.84);
      cursor: pointer;
      border: 2px solid rgba(0, 0, 0, 0.06);
      font-weight: 500;

      &.active {
        background: #1989fa;
        color: #fff;
        border-color: #1989fa;
      }
    }
  }

  // 底部操作按钮
  .footer-actions {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    background: #fff;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);

    .footer-btn {
      flex: 1;
      height: 88px;
      font-size: 28px;
      font-weight: 600;
      border-radius: 12px;
      border: none;

      &--later {
        color: rgba(0, 0, 0, 0.84);
        background: #f5f5f5;
      }

      &--confirm {
        background: #1a2b5e;
        color: #fff;
      }
    }
  }

  // 结果页（已确认 / 链接失效）
  .result-page {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px;
    flex: 1;

    .result-icon {
      font-size: 120px;
      margin-bottom: 24px;

      &--success {
        color: #07c160;
      }
    }

    .result-text {
      font-size: 26px;
      font-weight: 600;
      color: rgba(0, 0, 0, 0.84);
      margin: 0 0 12px;
    }

    .result-sub {
      font-size: 24px;
      color: rgba(0, 0, 0, 0.42);
      text-align: center;
      margin: 0;
    }
  }
}
</style>
